import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../../composants/Button/Button';
import OperationFormulaireAdmin from './OperationFormulaireAdmin';
import ModalArticles from '../../../composants/ModalArticles/ModalArticles';
import ValidationPopUp from '../../../composants/ValidationPopUp/ValidationPopUp';
import CGVAccept from '../../../composants/CGVAccept/CGVAccept';
import Banner from '../../../composants/Banner/Banner';
import AnalyseAccordion from '../../../composants/AnalyseAccordion/AnalyseAccordion';
import Coordonnees from '../../../composants/Coordonnees/Coordonnees';
import { PrintButton } from '../../../composants/PrintButton/PrintButton';
import ArticlesService, { Article } from '../../../services/articlesService';
import { useAuth } from '../../../contexts/AuthContext';
import { isSignataireRole } from '../../../utils/roleUtils';
import { formatPrice, getStatusColor, formatDate } from '../../../utils/entityUtils';
import { useLotImage } from '../../../utils/getLotImage';
import { StatusId, OperationBCI, Signataire } from '../types';
import { computeConsultationActions, operationActionsInternals } from '../domain/operationActions';
import {
  formatExistingArticles,
  isConsultationSectionReadOnly,
  computeConsultationStatus,
  validateConsultationForm,
  buildConsultationSaveBody,
  buildConsultationFormData,
  mapApiErrorsToForm,
} from '../domain/operationConsultation';
import {
  saveOperationDescriptif,
  updateArticleQuantity,
  removeArticle,
  transfertPrestataire,
  lancerConsultationRang,
  annulerOperation,
  notifierResponsable,
  loadSignataires,
  ConsultationValidationError,
} from '../services/operationBciService';
import '../../../composants/OperationConsultationBC/OperationConsultationBC.css';

const { isExpired: isOperationExpired } = operationActionsInternals;

const scrollToFirstError = (): void => {
  setTimeout(() => {
    const el = document.querySelector('.error, .error-message');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, 100);
};

interface OperationConsultationProps {
  commande: OperationBCI;
  rang: 1 | 2 | 3;
  errors: Record<string, string>;
  setErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  setCommande: React.Dispatch<React.SetStateAction<OperationBCI | null>>;
  handleLancerConsultation: (rang: number) => Promise<void>;
  handleAccepterCommande: (rang: number) => Promise<void>;
  handleRefuserCommande: (rang: number) => Promise<void>;
  acceptCGV?: boolean;
  setAcceptCGV?: React.Dispatch<React.SetStateAction<boolean>>;
  printRef?: any;
}

interface SelectedResponsable {
  id: number;
  type: string;
}

// Onglet Consultation acheteur-BC (V2) : preparation + lancement / transfert de la
// consultation d'un rang. Autonome : aucune dependance a l'ancienne grosse feuille.
// Boutons -> domain/operationActions ; derivations -> domain/operationConsultation ;
// appels API -> services/operationBcService.
const OperationConsultation: React.FC<OperationConsultationProps> = ({
  commande,
  rang,
  errors,
  setErrors,
  setCommande,
  handleLancerConsultation,
  handleAccepterCommande,
  handleRefuserCommande,
  acceptCGV = false,
  setAcceptCGV = () => { },
  printRef,
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isSignataire = isSignataireRole(user?.role?.toString() ?? '');
  const lotImage = useLotImage(
    commande.operation?.lot_technique?.nom || commande.lot_technique?.nom,
  );

  // Etats UI
  const [isSaving, setIsSaving] = useState(false);
  // Feedback inline (remplace les alert()) : succes/erreur affiche en banniere en tete.
  const [feedback, setFeedback] = useState<{ type: 'success' | 'danger'; message: string } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [articlesMode, setArticlesMode] = useState<'initial' | 'catalogue' | 'prestataire'>('initial');
  const [existingArticles, setExistingArticles] = useState<Article[]>([]);

  const [isArticlesModalOpen, setIsArticlesModalOpen] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isEnvoyerPrestataireModalOpen, setIsEnvoyerPrestataireModalOpen] = useState(false);
  const [isChangeModeModalOpen, setIsChangeModeModalOpen] = useState(false);
  const [isConfirmConsultationModalOpen, setIsConfirmConsultationModalOpen] = useState(false);
  const [pendingRangToConsult, setPendingRangToConsult] = useState<number | null>(null);

  const [isCGVModalOpen, setIsCGVModalOpen] = useState(false);
  const [readyToLaunchAfterCGV, setReadyToLaunchAfterCGV] = useState(false);
  const [readyToTransfertAfterCGV, setReadyToTransfertAfterCGV] = useState(false);

  const [isNotifyResponsableModalOpen, setIsNotifyResponsableModalOpen] = useState(false);
  const [responsables, setResponsables] = useState<Signataire[]>([]);
  const [selectedResponsable, setSelectedResponsable] = useState<SelectedResponsable | null>(null);
  const [responsablesLoading, setResponsablesLoading] = useState(false);

  const [chronoExpired, setChronoExpired] = useState(false);

  useEffect(() => {
    const handler = () => setChronoExpired(true);
    window.addEventListener('chronoExpired', handler);
    return () => window.removeEventListener('chronoExpired', handler);
  }, []);

  // Charge les articles du rang depuis la donnee (source : commande.commandes[rang].articles).
  // Deps limitees a (id, commandes, rang) volontairement : une edition de champ du formulaire
  // mute `formulaire` mais pas `commandes` ; elle ne doit donc PAS recharger la table et ecraser
  // les articles ajoutes via la modale (non encore refetches dans commande.commandes).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const loaded = formatExistingArticles(commande, rang);
    setExistingArticles(loaded);
    setArticlesMode((prev) => (loaded.length > 0 ? 'catalogue' : prev === 'prestataire' ? 'prestataire' : 'initial'));
  }, [commande.id, commande.commandes, rang]);

  const expired = useMemo(() => isOperationExpired(commande, chronoExpired), [commande, chronoExpired]);
  const hasExistingArticles = existingArticles.length > 0;
  const adminReadOnly = useMemo(() => isConsultationSectionReadOnly(commande, expired), [commande, expired]);
  const consultationStatus = useMemo(() => computeConsultationStatus(commande), [commande]);
  const formData = useMemo(() => buildConsultationFormData(commande), [commande]);

  const actions = useMemo(
    () =>
      computeConsultationActions(commande, rang, {
        isSignataire,
        articlesMode,
        hasArticlesPresent: hasExistingArticles,
        chronoExpired,
      }),
    [commande, rang, isSignataire, articlesMode, hasExistingArticles, chronoExpired],
  );

  // Notification de demande de validation deja envoyee (prefix 'Demande de validation').
  const notificationInfo = useMemo(() => {
    const operationId = commande.id;
    const historique = commande.historique || [];
    if (!operationId) return { sent: false, labels: [] as string[] };
    const events = historique.filter(
      (e) => e.prefix === 'Demande de validation' && e.type === `BDC.Operation.${operationId}`,
    );
    if (events.length > 0) {
      const labels = events
        .slice()
        .sort((a, b) => new Date(b.created_at || '').getTime() - new Date(a.created_at || '').getTime())
        .map((e) => e.label)
        .filter((l): l is string => l !== null && l !== undefined);
      return { sent: true, labels };
    }
    return { sent: false, labels: [] as string[] };
  }, [commande]);

  const hasArticles = useCallback(
    (): boolean => existingArticles.length > 0 || articlesMode === 'prestataire',
    [existingArticles, articlesMode],
  );

  // --- Mutations du formulaire ------------------------------------------------
  const handleFormDataChange = useCallback(
    (newData: Record<string, unknown>) => {
      setCommande((prev) =>
        prev ? { ...prev, formulaire: { ...prev.formulaire, ...newData } } : prev,
      );
    },
    [setCommande],
  );

  const handleBesoinChange = useCallback(
    (value: string) => {
      setCommande((prev) =>
        prev ? { ...prev, formulaire: { ...prev.formulaire, besoin: value } } : prev,
      );
    },
    [setCommande],
  );

  // --- Sauvegarde -------------------------------------------------------------
  const saveOperationData = useCallback(
    async (validateRequired: boolean): Promise<void> => {
      if (validateRequired) {
        const v = validateConsultationForm(commande.formulaire);
        if (!v.valid) {
          setErrors(v.errors);
          throw new Error('Veuillez remplir tous les champs obligatoires');
        }
      }
      try {
        await saveOperationDescriptif(commande.id, buildConsultationSaveBody(commande));
        setErrors({});
      } catch (error) {
        if (error instanceof ConsultationValidationError) {
          setErrors(mapApiErrorsToForm(error.validationErrors));
          scrollToFirstError();
          throw new Error('Erreurs de validation détectées');
        }
        throw error;
      }
    },
    [commande, setErrors],
  );

  // --- Articles (CRUD) --------------------------------------------------------
  const handleAddToCommand = useCallback((articles: Article[]) => {
    setExistingArticles(articles);
    setArticlesMode(articles.length > 0 ? 'catalogue' : 'initial');
  }, []);

  const handleExistingQuantityChange = useCallback(
    async (index: number, quantity: number) => {
      const validQuantity = Math.max(1, quantity);
      const article = existingArticles[index];
      if (!article) return;

      const updated = existingArticles.map((art, i) => {
        if (i !== index) return art;
        const prixUnitaire = ArticlesService.getPrixByQuantity(art, validQuantity);
        return { ...art, quantite: validQuantity, totalHT: prixUnitaire * validQuantity };
      });
      setExistingArticles(updated);

      try {
        const catalogueId = article.catalogueId || article.id;
        await updateArticleQuantity(commande.id, catalogueId, validQuantity);
      } catch {
        // Sauvegarde automatique silencieuse : pas d'alerte utilisateur.
      }
    },
    [existingArticles, commande.id],
  );

  const handleRemoveExistingArticle = useCallback(
    async (catalogueId: string) => {
      setDeletingId(catalogueId);
      try {
        await removeArticle(commande.id, catalogueId);
        const updated = existingArticles.filter(
          (a) => a.catalogueId !== catalogueId && a.id !== catalogueId,
        );
        setExistingArticles(updated);
        if (updated.length === 0) setArticlesMode('initial');
      } catch (error) {
        setFeedback({ type: 'danger', message: error instanceof Error ? error.message : "Erreur lors de la suppression de l'article" });
      } finally {
        setDeletingId(null);
      }
    },
    [existingArticles, commande.id],
  );

  // --- Lancement --------------------------------------------------------------
  const handleConfirmLaunch = useCallback(() => setIsConfirmModalOpen(true), []);

  const handleLancer = useCallback(async () => {
    if (!hasArticles()) {
      setFeedback({ type: 'danger', message: 'Vous devez créer au moins un article avant de lancer la consultation.' });
      return;
    }
    const v = validateConsultationForm(commande.formulaire);
    if (!v.valid) {
      setErrors(v.errors);
      scrollToFirstError();
      return;
    }
    try {
      setIsSaving(true);
      await saveOperationData(true);
      setIsSaving(false);
      if (!acceptCGV) {
        setReadyToLaunchAfterCGV(true);
        setIsCGVModalOpen(true);
      } else {
        handleConfirmLaunch();
      }
    } catch (error) {
      setIsSaving(false);
      const message = error instanceof Error ? error.message : 'Une erreur est survenue lors de la sauvegarde.';
      if (message !== 'Erreurs de validation détectées') {
        setFeedback({ type: 'danger', message: 'Erreur lors de la sauvegarde: ' + message });
      }
      scrollToFirstError();
    }
  }, [hasArticles, commande, setErrors, saveOperationData, acceptCGV, handleConfirmLaunch]);

  const handleConfirmLancerConsultation = useCallback(async () => {
    try {
      await saveOperationData(false);
      await handleLancerConsultation(rang);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      setFeedback({ type: 'danger', message: `Erreur lors du lancement de la consultation: ${message}` });
    }
  }, [saveOperationData, handleLancerConsultation, rang]);

  // --- Transfert au prestataire (panier inverse) ------------------------------
  const handleTransfertPrestataire = useCallback(async () => {
    const v = validateConsultationForm(commande.formulaire);
    if (!v.valid) {
      setErrors(v.errors);
      scrollToFirstError();
      return;
    }
    try {
      await saveOperationData(true);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erreur lors de la sauvegarde';
      if (message === 'Erreurs de validation détectées') {
        scrollToFirstError();
      } else {
        setFeedback({ type: 'danger', message: `Erreur lors de la sauvegarde: ${message}` });
      }
      return;
    }

    try {
      const result = await transfertPrestataire(commande.id);
      const data = result.data as Partial<OperationBCI> | undefined;
      if (data) {
        setCommande((prev) =>
          prev ? { ...prev, ...data, formulaire: { ...prev.formulaire, ...data.formulaire } } : prev,
        );
      } else {
        setCommande((prev) => (prev ? { ...prev, transferred: true } : prev));
      }
      setFeedback({ type: 'success', message: 'Demande envoyée au prestataire avec succès. Il définira les articles nécessaires.' });
      window.location.reload();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erreur lors du transfert au prestataire';
      setFeedback({ type: 'danger', message: `Erreur: ${message}. Veuillez réessayer.` });
    }
  }, [commande, setErrors, saveOperationData, setCommande]);

  const handleConfirmModeChange = useCallback(async () => {
    setIsChangeModeModalOpen(false);
    await handleTransfertPrestataire();
  }, [handleTransfertPrestataire]);

  // --- Validation par un signataire -------------------------------------------
  const loadResponsables = useCallback(async () => {
    setResponsablesLoading(true);
    try {
      setResponsables(await loadSignataires());
    } catch {
      setResponsables([]);
    } finally {
      setResponsablesLoading(false);
    }
  }, []);

  const handleNotifyResponsable = useCallback(async () => {
    setIsSaving(true);
    try {
      await saveOperationData(true);
      setIsNotifyResponsableModalOpen(true);
      loadResponsables();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Une erreur est survenue lors de la sauvegarde.';
      if (message === 'Erreurs de validation détectées' || message === 'Veuillez remplir tous les champs obligatoires') {
        scrollToFirstError();
      } else {
        setFeedback({ type: 'danger', message: message });
      }
    } finally {
      setIsSaving(false);
    }
  }, [saveOperationData, loadResponsables]);

  const handleConfirmNotifyResponsable = useCallback(async () => {
    if (!selectedResponsable) {
      setFeedback({ type: 'danger', message: 'Veuillez sélectionner un responsable' });
      return;
    }
    try {
      const result = await notifierResponsable(commande.id, selectedResponsable.id, selectedResponsable.type);
      setIsNotifyResponsableModalOpen(false);
      setSelectedResponsable(null);
      setFeedback({ type: 'success', message: `La demande a été envoyée avec succès à ${result.responsable?.prenom} ${result.responsable?.nom}.` });
      window.location.reload();
    } catch (error) {
      setFeedback({ type: 'danger', message: `Erreur lors de la demande au responsable: ${(error as Error).message}` });
    }
  }, [selectedResponsable, commande.id]);

  // --- Annulation / sortie ----------------------------------------------------
  const handleCancelOperation = useCallback(async () => {
    setIsDeleting(true);
    try {
      await annulerOperation(commande.id);
      navigate('/operations');
    } catch (error) {
      setFeedback({ type: 'danger', message: error instanceof Error ? error.message : "Erreur lors de l'annulation de l'opération." });
    } finally {
      setIsDeleting(false);
      setIsCancelModalOpen(false);
    }
  }, [commande.id, navigate]);

  const handleSaveAndQuit = useCallback(async () => {
    setIsSaving(true);
    try {
      await saveOperationData(false);
      navigate('/operations');
    } catch (error) {
      setFeedback({ type: 'danger', message: error instanceof Error ? error.message : 'Une erreur est survenue lors de la sauvegarde.' });
    } finally {
      setIsSaving(false);
    }
  }, [saveOperationData, navigate]);

  // --- Consultation du rang suivant (apres refus) -----------------------------
  const handleConfirmConsultationRangSuivant = useCallback(async () => {
    setIsConfirmConsultationModalOpen(false);
    const rangToRun = pendingRangToConsult;
    setPendingRangToConsult(null);
    if (!rangToRun) return;
    try {
      if (commande.transferred === true) {
        await transfertPrestataire(commande.id);
      } else {
        await lancerConsultationRang(commande.id);
      }
      window.location.reload();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erreur lors du lancement';
      setFeedback({ type: 'danger', message: `Erreur: ${message}. Veuillez réessayer.` });
    }
  }, [pendingRangToConsult, commande]);

  const isAnnule = commande.status?.id === StatusId.Annule;

  const printButton = (
    <PrintButton
      targetRef={printRef}
      fileName={`${commande.numero || commande.operation?.numero} - Consultation Rang ${rang}`}
      value="Imprimer la consultation"
      suiviHeaderProps={{
        titre: commande.operation?.nom || commande.nom || '',
        sousTitre: '',
        subtitle: (() => {
          const lt = commande.operation?.lot_technique || commande.lot_technique;
          const dep = commande.operation?.lot_departement?.departement || commande.lot_departement?.departement;
          return lt && dep ? `${lt.id} - ${lt.nom}, ${dep.id} - ${dep.nom}` : 'Informations de lot non disponibles';
        })(),
        status: commande.status?.nom || 'Statut inconnu',
        statusColor: getStatusColor(commande.status?.nom || ''),
        delais: commande.expire_at,
        backgroundImage: lotImage,
      }}
    />
  );

  const showActionRow =
    actions.annuler.visible ||
    actions.sauvegarderQuitter.visible ||
    actions.refuserOffre.visible ||
    actions.accepterOffre.visible ||
    actions.consulterRangSuivant.visible ||
    actions.demanderValidation.visible ||
    actions.lancerConsultation.visible;

  const launchTooltip = !acceptCGV
    ? 'Vous devez accepter les CG'
    : !hasArticles()
      ? 'Vous devez sélectionner au moins un article'
      : actions.lancerConsultation.reason || '';

  return (
    <>
      <div className="consultation-bc">
        {feedback && <Banner variant={feedback.type} message={feedback.message} />}
        {expired && !isAnnule && (
          <div
            className="expired-notice"
            style={{
              backgroundColor: '#fee',
              border: '2px solid #c33',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '24px',
              color: '#c33',
              fontWeight: 'bold',
              textAlign: 'center',
            }}
          >
            Cette commande est expirée. Vous ne pouvez plus modifier cette consultation.
          </div>
        )}

        {/* Descriptif Administratif */}
        <AnalyseAccordion title="Descriptif Administratif" defaultOpen>
          <div className="section-content">
            <OperationFormulaireAdmin
              operation={commande}
              formData={formData}
              viewMode={adminReadOnly}
              errors={errors}
              setFormData={!adminReadOnly ? handleFormDataChange : undefined}
            />
          </div>
        </AnalyseAccordion>

        {/* Descriptif Technique */}
        <AnalyseAccordion title="Descriptif Technique" defaultOpen>
          <div className="section-content">
            {!adminReadOnly ? (
              <div className="form-group">
                <label>
                  Cahier des charges <span style={{ color: 'red' }}>*</span>
                  <span className="desc-precision">
                    (Les liens URL vers des pièces graphiques ou des documents complémentaires peuvent être proposés
                    par l’acheteur à titre d’information, mais seules les informations écrites ci-dessous sont
                    contractuelles. Par ailleurs, toutes précisions administratives ou juridiques de nature à
                    contrevenir à l’accord-cadre général sans dérogations prévues au contrat sont à considérer comme
                    inexistantes dans ce descriptif)
                  </span>
                </label>
                <textarea
                  value={commande.formulaire?.besoin || ''}
                  onChange={(e) => handleBesoinChange(e.target.value)}
                  placeholder="Cahier des charges (obligatoire)"
                  rows={20}
                  className={errors.besoin ? 'error' : ''}
                />
                {errors.besoin && <span className="error-message">{errors.besoin}</span>}
              </div>
            ) : (
              <div className="form-group">
                <label>
                  Cahier des charges
                  <span className="desc-precision">
                    (Les liens URL vers des pièces graphiques ou des documents complémentaires peuvent être proposés
                    par l’acheteur à titre d’information, mais seules les informations écrites ci-dessous sont
                    contractuelles. Par ailleurs, toutes précisions administratives ou juridiques de nature à
                    contrevenir à l’accord-cadre général sans dérogations prévues au contrat sont à considérer comme
                    inexistantes dans ce descriptif)
                  </span>
                </label>
                <div className="input-inactif definition-besoin">
                  {commande.formulaire?.besoin || commande.operation?.besoin || 'Pas précisé'}
                </div>
              </div>
            )}
          </div>
        </AnalyseAccordion>

        {/* Descriptif Financier */}
        <AnalyseAccordion title="Descriptif Financier" defaultOpen>
          <div className="section-content">
            {(() => {
              const isTransferred = commande.transferred === true;
              const sid = commande.status?.id;
              const isTransfertAcheteurValidation =
                isTransferred &&
                (rang === 2 || rang === 3) &&
                (sid === StatusId.BrouillonAcheteur || sid === StatusId.AAnalyser);

              if (isTransferred && !isTransfertAcheteurValidation) {
                const reponseEvent = (commande.historique || [])
                  .filter((h) => h.prefix === 'Consultation')
                  .slice()
                  .sort(
                    (a, b) =>
                      new Date(b.updated_at || b.created_at || '').getTime() -
                      new Date(a.updated_at || a.created_at || '').getTime(),
                  )[0];
                return (
                  <div className="prestataire-mode">
                    <div className="message-content">
                      <h4>Consultation envoyée au prestataire de rang {rang}</h4>
                      <p>
                        Le prestataire a reçu votre consultation. Vous serez informé de sa réponse dans les 10 jours
                        maximum.
                      </p>
                      <div className="status-indicator">
                        <span className="status-dot"></span>
                        Consultation reçue le{' '}
                        {reponseEvent ? formatDate(reponseEvent.updated_at || reponseEvent.created_at || '') : 'N/A'}
                      </div>
                    </div>
                  </div>
                );
              }

              if (adminReadOnly) {
                if (hasExistingArticles) {
                  return (
                    <div className="articles-display">
                      <div className="articles-table-wrapper">
                        <table>
                          <thead>
                            <tr>
                              <th>N° Réf</th>
                              <th>Description de la prestation</th>
                              <th>Unité</th>
                              <th>Quantité</th>
                              <th>Prix unitaire HT</th>
                              <th>Total HT</th>
                            </tr>
                          </thead>
                          <tbody>
                            {existingArticles.map((article) => (
                              <tr key={article.id}>
                                <td>{article.code}</td>
                                <td>{article.nom}</td>
                                <td>{article.unite}</td>
                                <td>{article.quantite}</td>
                                <td>{formatPrice(article.prix, 4, true)}</td>
                                <td>{formatPrice(article.totalHT, 4, true)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <div className="total-section">
                        <p>
                          Total HT (Rang {rang}) :{' '}
                          {formatPrice(existingArticles.reduce((sum, a) => sum + a.totalHT, 0), 4, true)}
                        </p>
                      </div>
                    </div>
                  );
                }
                return (
                  <div className="no-articles-message">
                    <p>Aucun article défini pour cette consultation.</p>
                  </div>
                );
              }

              // Mode edition (brouillon acheteur)
              return (
                <>
                  {hasExistingArticles && (
                    <div className="articles-table-container">
                      {actions.changerMode.visible && (
                        <div className="changer-de-mode">
                          <Button
                            value="Changer de mode"
                            variant="secondary"
                            onClick={() => {
                              if (existingArticles.length > 0) setIsChangeModeModalOpen(true);
                              else setArticlesMode('initial');
                            }}
                          />
                        </div>
                      )}

                      <div className="articles-table-wrapper">
                        <table>
                          <thead>
                            <tr>
                              <th>N° Réf</th>
                              <th>Description de la prestation</th>
                              <th>Unité</th>
                              <th>Quantité</th>
                              <th>Prix unitaire HT</th>
                              <th>Total HT</th>
                              {actions.changerMode.visible && <th>Actions</th>}
                            </tr>
                          </thead>
                          <tbody>
                            {existingArticles.map((article) => (
                              <tr key={article.id}>
                                <td className="article-number">{article.code}</td>
                                <td className="article-description">
                                  <div className="article-name">{article.nom}</div>
                                </td>
                                <td className="article-unit">{article.unite}</td>
                                <td className="article-quantity">
                                  <div className="quantity-cell">{article.quantite}</div>
                                </td>
                                <td className="article-price price-cell">
                                  {formatPrice(ArticlesService.getPrixByQuantity(article, article.quantite), 4, true)}
                                </td>
                                <td className="article-total total-cell">{formatPrice(article.totalHT, 4, true)}</td>
                                {actions.changerMode.visible && (
                                  <td className="article-actions">
                                    <button
                                      className="delete-button"
                                      onClick={() => handleRemoveExistingArticle(article.catalogueId || article.id)}
                                      type="button"
                                      disabled={deletingId !== null}
                                    >
                                      {deletingId === (article.catalogueId || article.id) ? (
                                        <span className="delete-spinner" aria-label="Suppression en cours" />
                                      ) : (
                                        '×'
                                      )}
                                    </button>
                                  </td>
                                )}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {actions.ajouterArticle.visible && (
                          <button
                            className="add-article-button"
                            type="button"
                            onClick={() => setIsArticlesModalOpen(true)}
                          >
                            Ajouter ou modifier un article
                          </button>
                        )}
                      </div>

                      <div className="articles-actions">
                        <div className="total-section">
                          <p>
                            Total HT (Rang {rang}):{' '}
                            {formatPrice(existingArticles.reduce((sum, a) => sum + a.totalHT, 0), 4, true)}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {!hasExistingArticles && actions.choisirCatalogue.visible && (
                    <div className="financial-choice">
                      <h4>Comment souhaitez-vous définir les articles ?</h4>
                      <div className="choice-buttons">
                        <Button
                          value="Choisir des articles du catalogue"
                          variant="primary"
                          onClick={() => {
                            setArticlesMode('catalogue');
                            setIsArticlesModalOpen(true);
                          }}
                        />
                        <Button
                          value="Laisser le prestataire remplir les articles"
                          variant="secondary"
                          onClick={() => setIsEnvoyerPrestataireModalOpen(true)}
                        />
                      </div>
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        </AnalyseAccordion>

        {/* Contact et documents */}
        <AnalyseAccordion title="Contact et documents" defaultOpen>
          <div className="section-content coordinates-section">
            <Coordonnees
              operation={commande}
              prestataire={commande.commandes?.[rang - 1]}
              isAcheteur
              isPresta={false}
              showSignataire={false}
            />
          </div>
        </AnalyseAccordion>

        {/* Encart consultation lancee / transferee */}
        {consultationStatus.isVisible && (
          <section>
            <div className="section-content consultation-info">
              <p>
                Consultation lancée au rang {rang} le{' '}
                {formatDate(commande.updated_at || commande.created_at || '')} par{' '}
                {commande.organisme?.nom || 'Organisme'}
              </p>
            </div>
            {consultationStatus.showOffreEnvoyee && (
              <div className="section-content consultation-info">
                <p className="offre-envoyee-message">
                  {`Offre envoyée le ${commande.updated_at ? formatDate(commande.updated_at) : 'date non disponible'} par le prestataire ${commande.commandes?.[rang - 1]?.contrat?.entite?.nom || 'Prestataire'} de rang ${rang}`}
                </p>
              </div>
            )}
          </section>
        )}

        {/* Message d'annulation */}
        {isAnnule && (
          <section>
            <div className="section-content consultation-info">
              <p>
                {(() => {
                  const ev = (commande.historique || []).find((h) => h.prefix === 'Annulation');
                  if (!ev) return 'Projet de bon de commande annulé';
                  const d = new Date(ev.created_at || ev.updated_at || '');
                  const dateStr = d.toLocaleDateString('fr-FR');
                  const timeStr = `${d.getHours().toString().padStart(2, '0')}h${d.getMinutes().toString().padStart(2, '0')}m${d.getSeconds().toString().padStart(2, '0')}s`;
                  return `Projet de bon de commande annulé le ${dateStr} à ${timeStr} par l'acheteur`;
                })()}
              </p>
            </div>
          </section>
        )}

        {/* BCI : un seul bouton « Refuser l'offre » dans la consultation, rendu IDENTIQUE a
            l'onglet Offre (meme wrapper .action-buttons-wrapper + meme variant/size -> meme taille).
            Les autres actions BC (annuler, sauvegarder, lancer, accepter, validation, consulter rang
            suivant) ne s'appliquent pas au flux BCI (1 commande importee). */}
        <div className="action-buttons-wrapper">
          {showActionRow && actions.refuserOffre.visible && (
            <div className="action-buttons-row">
              <Button
                value="Refuser l'offre"
                variant="secondary"
                onClick={() => handleRefuserCommande(rang)}
                disabled={actions.refuserOffre.disabled}
              />
            </div>
          )}
        </div>

        {/* Modales */}
        {isArticlesModalOpen && (
          <ModalArticles
            isOpen={isArticlesModalOpen}
            onClose={() => {
              setIsArticlesModalOpen(false);
              if (existingArticles.length === 0) setArticlesMode('initial');
            }}
            commandes={commande.commandes || []}
            lotNom={commande.lot_technique?.nom || 'Catalogue'}
            onAddToCommand={handleAddToCommand}
            existingSelection={existingArticles}
            prestataireRang={Number(commande.commandes?.[0]?.rang ?? rang)}
          />
        )}

        <CGVAccept
          acceptCGV={acceptCGV}
          setAcceptCGV={setAcceptCGV}
          isOpen={isCGVModalOpen}
          onClose={() => {
            setIsCGVModalOpen(false);
            setReadyToLaunchAfterCGV(false);
            setReadyToTransfertAfterCGV(false);
          }}
          onConfirm={() => {
            setAcceptCGV(true);
            setTimeout(() => {
              setIsCGVModalOpen(false);
              if (readyToLaunchAfterCGV) {
                setReadyToLaunchAfterCGV(false);
                handleConfirmLaunch();
              } else if (readyToTransfertAfterCGV) {
                setReadyToTransfertAfterCGV(false);
                setIsEnvoyerPrestataireModalOpen(true);
              }
            }, 100);
          }}
        />

        <ValidationPopUp
          isOpen={isConfirmModalOpen}
          title="Lancement de la consultation"
          text={`Vous vous apprêtez à lancer la consultation pour le prestataire de rang ${rang}. Cette action est irréversible.`}
          onClose={() => setIsConfirmModalOpen(false)}
          onConfirm={async () => {
            setIsConfirmModalOpen(false);
            await handleConfirmLancerConsultation();
          }}
          cancelLabel="Annuler"
          confirmLabel="Confirmer"
        />

        <ValidationPopUp
          isOpen={isConfirmConsultationModalOpen}
          title={`Consulter le prestataire de rang ${pendingRangToConsult ?? ''}`}
          text={`Êtes-vous sûr de vouloir lancer la consultation pour le prestataire de rang ${pendingRangToConsult ?? ''} ?`}
          onClose={() => {
            setIsConfirmConsultationModalOpen(false);
            setPendingRangToConsult(null);
          }}
          onConfirm={handleConfirmConsultationRangSuivant}
          cancelLabel="Non"
          confirmLabel="Oui"
        />

        <ValidationPopUp
          isOpen={isEnvoyerPrestataireModalOpen}
          title="Envoyer une demande au prestataire"
          text={`Vous vous apprêtez à envoyer une demande au prestataire de Rang ${rang} pour qu'il remplisse les articles pour vous. Attention, vous ne pourrez plus revenir sur le descriptif Administratif et Technique. Cette action est irréversible.`}
          onClose={() => setIsEnvoyerPrestataireModalOpen(false)}
          onConfirm={async () => {
            if (!acceptCGV) {
              setIsEnvoyerPrestataireModalOpen(false);
              setReadyToTransfertAfterCGV(true);
              setIsCGVModalOpen(true);
              return;
            }
            setIsEnvoyerPrestataireModalOpen(false);
            await handleTransfertPrestataire();
          }}
          cancelLabel="Annuler"
          confirmLabel="Valider"
        />

        <ValidationPopUp
          isOpen={isCancelModalOpen}
          title="Annulation de la commande"
          text={
            commande.status?.id === StatusId.BrouillonAcheteur
              ? 'Êtes-vous sûr de vouloir annuler et supprimer cette commande ? Cette action est irréversible.'
              : "Êtes-vous sûr de vouloir annuler cette commande ? L'opération et les commandes liées passeront en statut annulé. Cette action est irréversible."
          }
          onClose={() => setIsCancelModalOpen(false)}
          onConfirm={handleCancelOperation}
          cancelLabel="Non"
          confirmLabel="Oui, annuler"
        />

        <ValidationPopUp
          isOpen={isChangeModeModalOpen}
          title="Laisser le prestataire remplir les articles"
          text="Attention, en changeant de mode, tous vos articles seront supprimés et remplacés par ceux du prestataire. Cette action est irréversible."
          onClose={() => setIsChangeModeModalOpen(false)}
          onConfirm={handleConfirmModeChange}
          cancelLabel="Annuler"
          confirmLabel="Confirmer"
        />

        <ValidationPopUp
          isOpen={isNotifyResponsableModalOpen}
          title="Obtenir la validation de l'acheteur signataire"
          text=""
          onClose={() => {
            setIsNotifyResponsableModalOpen(false);
            setSelectedResponsable(null);
          }}
          onConfirm={handleConfirmNotifyResponsable}
          cancelLabel="Annuler"
          confirmLabel="Envoyer la demande de validation"
        >
          <div className="mt-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Sélectionnez le responsable à qui envoyer la demande :
            </label>
            {responsablesLoading ? (
              <p className="text-gray-500">Chargement...</p>
            ) : responsables.length === 0 ? (
              <p className="text-red-500">Aucun responsable disponible</p>
            ) : (
              <select
                style={{ marginTop: '16px', marginBottom: '24px' }}
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={selectedResponsable ? `${selectedResponsable.type}-${selectedResponsable.id}` : ''}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value) {
                    const [type, id] = value.split('-');
                    setSelectedResponsable({ id: parseInt(id, 10), type });
                  } else {
                    setSelectedResponsable(null);
                  }
                }}
              >
                <option value="">-- Choisir un responsable --</option>
                {responsables.map((resp) => (
                  <option key={`${resp.type}-${resp.id}`} value={`${resp.type}-${resp.id}`}>
                    {`${(resp.prenom || '').charAt(0).toUpperCase() + (resp.prenom || '').slice(1).toLowerCase()} ${(resp.nom || '').toUpperCase()}`}{' '}
                    - {resp.type === 'responsable_achat' ? 'Administrateur' : 'Signataire'}
                    {resp.fonction ? ` (${resp.fonction})` : ''}
                  </option>
                ))}
              </select>
            )}
            {selectedResponsable && (
              <p className="mt-2 text-sm text-gray-600">
                Email :{' '}
                {
                  responsables.find(
                    (r) => r.id === selectedResponsable.id && r.type === selectedResponsable.type,
                  )?.email_contact
                }
              </p>
            )}
          </div>
        </ValidationPopUp>
      </div>
    </>
  );
};

export default OperationConsultation;
